import React from "react";
import { motion } from "framer-motion";
import { MapPin, Navigation, Info } from "lucide-react";

// Build Google Maps URLs from clean address strings.
// `dir/?api=1&destination=...` is Google's official cross-platform format
// (works on desktop browsers + iOS Google Maps app + Android Maps app).
const FOUGERES_ADDRESS = "20 Boulevard Nelson Mandela, 35300 Fougères, France";
const LOUVIGNE_ADDRESS = "La Morinais, 35420 Louvigné-du-Désert, France";

const buildDirectionsUrl = (address) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    address
  )}`;

const buildEmbedSrc = (address) =>
  `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;

const SITES = [
  {
    id: "fougeres",
    name: "Paintball Fougères",
    address: FOUGERES_ADDRESS,
    addressShort: "20 Bd Nelson Mandela · 35300",
    badge: "Second site",
    accent: "#E91E63",
    embedSrc: buildEmbedSrc(FOUGERES_ADDRESS),
    directionsUrl: buildDirectionsUrl(FOUGERES_ADDRESS),
    note: null,
  },
  {
    id: "louvigne",
    name: "Paintball Louvigné-du-Désert",
    address: LOUVIGNE_ADDRESS,
    addressShort: "La Morinais · 35420",
    badge: "Site historique",
    accent: "#00E676",
    embedSrc: buildEmbedSrc(LOUVIGNE_ADDRESS),
    directionsUrl: buildDirectionsUrl(LOUVIGNE_ADDRESS),
    note: "Nous nous situons dans le virage.",
  },
];

export default function Maps() {
  return (
    <section
      id="acces"
      data-testid="maps-section"
      className="relative bg-white py-20 md:py-28 lg:py-32 border-t-2 border-black"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
          <div>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-[#E91E63] mb-3">
              06 — Accès &amp; Itinéraires
            </p>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase leading-[0.95] tracking-tight">
              Comment <span className="text-[#E91E63]">nous trouver</span>
            </h2>
          </div>
          <p className="text-base md:text-lg text-gray-700 max-w-md">
            Cliquez sur le bouton « Itinéraire » pour ouvrir Google Maps et obtenir le trajet en un clic depuis votre position.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-10">
          {SITES.map((s, i) => (
            <motion.article
              key={s.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`map-card-${s.id}`}
              className="border-2 border-black bg-white overflow-hidden flex flex-col"
            >
              {/* Map iframe */}
              <div className="relative aspect-[16/10] bg-gray-100">
                <iframe
                  src={s.embedSrc}
                  title={`Carte ${s.name}`}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid={`map-iframe-${s.id}`}
                  className="absolute inset-0 w-full h-full border-0"
                  allowFullScreen
                />
              </div>

              <div className="p-6 md:p-8 flex flex-col gap-4 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-black border-2 border-black"
                      style={{ background: s.accent }}
                    >
                      {s.badge}
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl font-bold uppercase leading-tight tracking-tight">
                      {s.name}
                    </h3>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 text-sm font-semibold text-gray-900">
                  <MapPin
                    className="w-4 h-4 mt-0.5 text-[#E91E63] shrink-0"
                    strokeWidth={2.5}
                  />
                  <address className="not-italic">{s.address}</address>
                </div>

                {s.note && (
                  <div
                    data-testid={`map-note-${s.id}`}
                    className="flex items-start gap-2 bg-[#FFEB3B] border-2 border-black p-3 text-sm font-semibold"
                  >
                    <Info
                      className="w-4 h-4 mt-0.5 shrink-0"
                      strokeWidth={2.5}
                    />
                    <p>{s.note}</p>
                  </div>
                )}

                <a
                  href={s.directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`map-directions-${s.id}`}
                  className="mt-auto inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-[#E91E63] text-white font-bold uppercase text-sm tracking-wider hover:-translate-y-0.5 hover:shadow-[0_8px_0_-2px_#0a0a0a] transition-all self-start"
                >
                  <Navigation className="w-4 h-4" strokeWidth={2.5} />
                  Obtenir l&apos;itinéraire
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
